import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #intersectionObserver = null;

  #visibilityHandler = null;

  #inView = false;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.#inView = Boolean(entries[0]?.isIntersecting);
        this.#syncPlayback();
      },
      { rootMargin: '0px' }
    );
    this.#intersectionObserver.observe(this);

    this.#visibilityHandler = () => {
      this.#syncPlayback();
    };
    document.addEventListener('visibilitychange', this.#visibilityHandler);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (this.#visibilityHandler) {
      document.removeEventListener('visibilitychange', this.#visibilityHandler);
      this.#visibilityHandler = null;
    }
    this.refs.videoElement?.pause();
  }

  #syncPlayback() {
    const videoElement = this.refs.videoElement;
    if (!videoElement) return;

    if (this.#inView && !document.hidden) {
      videoElement.play().catch(() => {});
      return;
    }

    videoElement.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
